import { supabase } from './supabase';
import { STAMP_DEFINITIONS, type StampDefinition, type StampStatKey } from '../data/challengeStamps';

/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * WHO HOLDS WHICH STAMP — the admin-side answer
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Reads the rating corpus once, folds it into the four per-user aggregates the stamps are defined
 * over, and runs {@link STAMP_DEFINITIONS} across every rater. No RPC, no migration, no write.
 *
 * ── SCOPE: WIDER THAN THE APP'S, ON PURPOSE ──────────────────────────────────────────────────────
 * The dashboard signs in with the anon key and is protected by RLS, so what it sees is what an
 * ADMIN can see. On `logs` that is the `logs_select_admin` policy — a bare `is_admin()`, verified
 * against production 2026-08-23 — which is strictly wider than the app's public slice: it does not
 * require `is_public`, and it ignores `is_silenced` and the block table.
 *
 * This module therefore counts:
 *
 *     is_event = false AND post_kind = 'rating' AND retired_at IS NULL AND deleted_at IS NULL
 *
 * and NOTHING about visibility. Retired and soft-deleted rows are still excluded, because
 * retire-and-create would otherwise double-count a re-rated venue and a deleted log is not a
 * rating — those two filters are about what a rating IS, not about who may see it.
 *
 * ⚠️ THE NUMBERS HERE WILL NOT MATCH THE APP. Measured on production 2026-08-23, both scopes
 * computed with the same SQL:
 *
 *                        admin scope   app's public slice
 *     rating rows              1,196                1,101
 *     raters                     209                  204
 *     🥂 Cheers                  209                  204
 *     🧳 Traveler                 56                   52
 *     🕺 Regular                  35                   32
 *     🍻 Bar Hopper                7                    5
 *     ✈️ Frequent Flyer            2                    2
 *     🌍 Global Citizen            1                    1
 *     🪩 Clubber                   1                    1
 *     🌙 Night Owl                 0                    0
 *     👑 Elite                     0                    0
 *
 * The public column reproduces the app's own measurements exactly, which is what confirms the
 * criteria mirrored in `src/data/challengeStamps.ts` are faithful. The admin column is the one this
 * screen renders — the whole point of putting this in the dashboard is seeing the private half too.
 *
 * ── HOW EACH AGGREGATE IS DERIVED (matched to the app, helper by helper) ─────────────────────────
 *  · venuesRated — distinct `profileRankingDedupeKey` (`lib/rankingDedupe.ts`): venue_id, else
 *    google_place_id, else apple_place_id, else venue_name, else the log id.
 *  · barsRated / clubsRated — distinct PLACE key (venue_id / google / apple only, never the name)
 *    where `logOwnedBarClubLabel` (`lib/profileVenueType.ts`) says bar / club, i.e. the log's own
 *    `venue_type`, with no `venues` override.
 *  · citiesRated — `venues.city_country`, else `composeFsqCityLabel(locality, region)`
 *    (`lib/cityLocaleLabel.ts`), lowercased. A venue with no label contributes nothing, so this is
 *    a FLOOR, never an overcount.
 *
 * `streakWeeks` and `continentsVisited` are NOT computed: no stamp in the set has read either since
 * the 2026-08-01 revision. If a time-gated stamp comes back, `created_at` is already on the row.
 *
 * ── COST ────────────────────────────────────────────────────────────────────────────────────────
 * Two reads: the rating corpus (1,196 rows today, so one 1,000-row page plus a short second) and
 * `profiles` for the raters who earned something. Fetched ON DEMAND — the panel does not load until
 * it is expanded — and held for the life of the page.
 * ──────────────────────────────────────────────────────────────────────────────────────────────── */

/** Runaway guard, not a working limit: prod holds 1,241 rows in `logs` in total. */
const MAX_LOG_ROWS = 20000;
/** PostgREST page size. Same 1,000 `fetchAllRows` uses — this file only pages by hand for the cap. */
const LOG_PAGE = 1000;
/** `profiles` ids go out as a PostgREST `in.(…)` list; ~37 chars per uuid keeps this well under 8 kB. */
const PROFILE_CHUNK = 120;

type EmbeddedVenue = { city_country: string | null; locality: string | null; region: string | null };

type StampLogRow = {
  id: string;
  user_id: string | null;
  venue_id: string | null;
  google_place_id: string | null;
  apple_place_id: string | null;
  venue_name: string | null;
  venue_type: string | null;
  /** PostgREST returns an embed as an object, or as a one-element array on some relationship shapes. */
  venue: EmbeddedVenue | EmbeddedVenue[] | null;
};

type ProfileRow = {
  id: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  is_private: boolean | null;
  city: string | null;
  university: string | null;
  created_at: string | null;
};

/** One person on a stamp's roster. Carries every stat so the table can show WHY they qualified. */
export type StampHolder = {
  id: string;
  username: string | null;
  fullName: string | null;
  avatarUrl: string | null;
  isPrivate: boolean | null;
  city: string | null;
  university: string | null;
  createdAt: string | null;
  venuesRated: number;
  citiesRated: number;
  barsRated: number;
  clubsRated: number;
};

export type StampHoldersResult = {
  /** Accounts with at least one qualifying rating — the denominator every percentage uses. */
  ratersEvaluated: number;
  /** Qualifying rating rows read. */
  ratingsEvaluated: number;
  /** badge id → holders, ranked by venues rated then name. */
  holdersByStampId: Record<string, StampHolder[]>;
};

function embeddedVenue(v: StampLogRow['venue']): EmbeddedVenue | null {
  if (!v) return null;
  return Array.isArray(v) ? (v[0] ?? null) : v;
}

/** Mirrors `composeFsqCityLabel` in the app (`lib/cityLocaleLabel.ts`). */
function composeFsqCityLabel(locality: string | null, region: string | null): string {
  const city = (locality ?? '').trim();
  if (!city) return '';
  const reg = (region ?? '').trim();
  if (!reg) return city;
  const cityU = city.toUpperCase();
  const regU = reg.toUpperCase();
  if (cityU === regU || cityU.endsWith(` ${regU}`)) return city;
  return `${city}, ${reg}`;
}

/** Mirrors `profileRankingDedupeKey` in the app (`lib/rankingDedupe.ts`). */
function venueDedupeKey(r: StampLogRow): string {
  return (
    (r.venue_id ?? '').trim() ||
    (r.google_place_id ?? '').trim() ||
    (r.apple_place_id ?? '').trim() ||
    (r.venue_name ?? '').trim() ||
    r.id
  );
}

/** Stable place identity. Deliberately EXCLUDES the name — see `loadChallengeFriendBadgeCounts`. */
function placeKey(r: StampLogRow): string {
  return (
    (r.venue_id ?? '').trim() || (r.google_place_id ?? '').trim() || (r.apple_place_id ?? '').trim()
  );
}

type Acc = {
  venues: Record<string, true>;
  cities: Record<string, true>;
  bars: Record<string, true>;
  clubs: Record<string, true>;
};

function countKeys(o: Record<string, true>): number {
  return Object.keys(o).length;
}

/** `true` when every clause of the stamp passes. The compound 👑 Elite is the only multi-clause one. */
export function stampEarned(
  def: StampDefinition,
  stats: Record<StampStatKey, number>,
): boolean {
  return def.criteria.every((c) => stats[c.stat] >= c.threshold);
}

export async function loadStampHolders(): Promise<StampHoldersResult> {
  /**
   * Paged by hand rather than through `fetchAllRows`, for one reason: the {@link MAX_LOG_ROWS} cap.
   * `fetchAllRows` decides when to stop from the chunk length alone, so clamping its `to` would
   * hand it a full page forever and it would never terminate at the ceiling.
   */
  const logs: StampLogRow[] = [];
  for (let offset = 0; offset < MAX_LOG_ROWS; offset += LOG_PAGE) {
    const { data, error } = await supabase
      .from('logs')
      .select(
        'id, user_id, venue_id, google_place_id, apple_place_id, venue_name, venue_type, venue:venues!venue_id(city_country, locality, region)',
      )
      /** ⚠️ Visibility is intentionally NOT filtered — see the scope note at the top of this file. */
      .eq('is_event', false)
      .eq('post_kind', 'rating')
      .is('retired_at', null)
      .is('deleted_at', null)
      /** Stable order, so consecutive pages cannot skip or repeat a row. */
      .order('id', { ascending: true })
      .range(offset, Math.min(offset + LOG_PAGE, MAX_LOG_ROWS) - 1);
    if (error) throw new Error(error.message);
    const chunk = (data ?? []) as unknown as StampLogRow[];
    for (const r of chunk) logs.push(r);
    if (chunk.length < LOG_PAGE) break;
  }

  const accByUser: Record<string, Acc> = {};
  for (const row of logs) {
    const uid = (row.user_id ?? '').trim();
    if (!uid) continue;
    let acc = accByUser[uid];
    if (!acc) {
      acc = { venues: {}, cities: {}, bars: {}, clubs: {} };
      accByUser[uid] = acc;
    }

    acc.venues[venueDedupeKey(row)] = true;

    const pk = placeKey(row);
    const vt = (row.venue_type ?? '').trim().toLowerCase();
    if (pk && vt === 'bar') acc.bars[pk] = true;
    if (pk && vt === 'club') acc.clubs[pk] = true;

    const v = embeddedVenue(row.venue);
    const cityLabel = ((v?.city_country ?? '').trim() || composeFsqCityLabel(v?.locality ?? null, v?.region ?? null)).trim();
    if (cityLabel) acc.cities[cityLabel.toLowerCase()] = true;
  }

  const raterIds = Object.keys(accByUser);
  const statsByUser: Record<string, Record<StampStatKey, number>> = {};
  for (const uid of raterIds) {
    const a = accByUser[uid];
    statsByUser[uid] = {
      venuesRated: countKeys(a.venues),
      citiesRated: countKeys(a.cities),
      barsRated: countKeys(a.bars),
      clubsRated: countKeys(a.clubs),
    };
  }

  /** Only look up the people who actually earned something — 🥂 Cheers means that is nearly all. */
  const earnedIdsByStamp: Record<string, string[]> = {};
  const namedIds: string[] = [];
  for (const uid of raterIds) {
    const stats = statsByUser[uid];
    let any = false;
    for (const def of STAMP_DEFINITIONS) {
      if (!stampEarned(def, stats)) continue;
      (earnedIdsByStamp[def.id] = earnedIdsByStamp[def.id] ?? []).push(uid);
      any = true;
    }
    if (any) namedIds.push(uid);
  }

  const profileById: Record<string, ProfileRow> = {};
  for (let i = 0; i < namedIds.length; i += PROFILE_CHUNK) {
    const chunk = namedIds.slice(i, i + PROFILE_CHUNK);
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, full_name, avatar_url, is_private, city, university, created_at')
      .in('id', chunk);
    if (error) throw new Error(error.message);
    for (const p of (data ?? []) as ProfileRow[]) profileById[p.id] = p;
  }

  const holdersByStampId: Record<string, StampHolder[]> = {};
  for (const def of STAMP_DEFINITIONS) {
    const ids = earnedIdsByStamp[def.id] ?? [];
    const rows: StampHolder[] = [];
    for (const uid of ids) {
      const p = profileById[uid];
      /** A rater with no readable `profiles` row is dropped rather than rendered nameless. */
      if (!p) continue;
      const s = statsByUser[uid];
      rows.push({
        id: uid,
        username: p.username,
        fullName: p.full_name,
        avatarUrl: p.avatar_url,
        isPrivate: p.is_private,
        city: p.city,
        university: p.university,
        createdAt: p.created_at,
        venuesRated: s.venuesRated,
        citiesRated: s.citiesRated,
        barsRated: s.barsRated,
        clubsRated: s.clubsRated,
      });
    }
    rows.sort(
      (a, b) =>
        b.venuesRated - a.venuesRated ||
        (a.username ?? a.fullName ?? '').localeCompare(b.username ?? b.fullName ?? ''),
    );
    holdersByStampId[def.id] = rows;
  }

  return {
    ratersEvaluated: raterIds.length,
    ratingsEvaluated: logs.length,
    holdersByStampId,
  };
}
